import { z } from 'zod';

import {
  calculationDataSchema,
  documentYearQuarterSchema,
  lineSchema,
  paymentTermSchema,
  peppolStatusSchema,
  transportTypeSchema,
} from '../common/schemas';

export const invoiceStatusSchema = z.enum(['Draft', 'InReview', 'LockedByUser', 'Approved', 'Expired', 'CoPaid', 'FcPaid', 'Credited']);

export const followUpRemarkSchema = z.object({
  causerName: z.string(),
  causerType: z.enum(['user', 'admin']),
  message: z.string(),
});

export const paymentFollowUpSchema = z.object({
  id: z.number(),
  remark: followUpRemarkSchema.nullable(),
  timestamp: z.string(),
  type: z.enum([
    'FollowUpStarted',
    'FollowUpManualReminder',
    'FollowUpContactClientByPhone',
    'FollowUpLastManualReminder',
    'FollowUpFormalNoticeSent',
    'FollowUpInvoiceSentToLawyer',
    'FollowUpInvoiceSentToBailiff',
    'FollowUpRemark',
  ]),
});

export const draftInvoiceSchema = z.object({
  id: z.number(),
  status: invoiceStatusSchema.extract(['Draft']),
  title: z.string().nullable(),
  description: z.string().nullable(),
  clientId: z.number().nullable(),
  contactId: z.number().nullable(),
  brandId: z.number().nullable(),
  numberingSeriesId: z.number().nullable(),
  conditionId: z.number().nullable(),
  customConditionId: z.number().nullable(),
  customAgreementId: z.number().nullable(),
  invoiceTemplateId: z.number().nullable(),
  paymentTerm: paymentTermSchema.nullable(),
  paymentTermEndOfMonth: z.boolean(),
  clientReference: z.string().nullable(),
  specialConditions: z.string().nullable(),
  userComments: z.string().nullable(),
  invoiceLines: z.array(lineSchema),
  coEntryNumber: z.null(),
  fcEntryNumber: z.null(),
  documentDate: z.null(),
  dueDate: z.null(),
  coTransportType: z.null(),
  coPeppolStatus: z.null(),
  coPartiallyPaid: z.literal(false),
  coPaidAmounts: z.array(z.never()),
  coPaidDate: z.null(),
  fcTransportType: z.null(),
  fcPeppolStatus: z.null(),
  fcPaidDate: z.null(),
  creditNoteDetails: z.null(),
  calculationData: calculationDataSchema,
  allowsAutomaticApproval: z.boolean(),
  hasPaymentFollowUp: z.literal(false),
  updatedAt: z.iso.datetime({ offset: true }),
});

const nonCreditedInvoiceSchema = z.object({
  id: z.number(),
  status: invoiceStatusSchema.exclude(['Draft', 'Credited']),
  title: z.string(),
  description: z.string().nullable(),
  clientId: z.number(),
  contactId: z.number(),
  brandId: z.number(),
  numberingSeriesId: z.number().nullable(), // Old invoices don't have a numbering series id
  conditionId: z.number().nullable(),
  customConditionId: z.number().nullable(),
  customAgreementId: z.number().nullable(),
  invoiceTemplateId: z.number().nullable(),
  paymentTerm: paymentTermSchema,
  paymentTermEndOfMonth: z.boolean(),
  clientReference: z.string().nullable(),
  specialConditions: z.string().nullable(),
  userComments: z.string().nullable(),
  invoiceLines: z.array(lineSchema).min(1),
  coEntryNumber: z.string().nullable(),
  fcEntryNumber: z.string(),
  documentDate: z.iso.datetime({ offset: true }),
  dueDate: z.iso.datetime({ offset: true }),
  coTransportType: transportTypeSchema.nullable(),
  coPeppolStatus: peppolStatusSchema.nullable(),
  coPartiallyPaid: z.boolean(),
  coPaidAmounts: z.array(z.object({ date: z.string(), amount: z.number() })),
  coPaidDate: z.iso.datetime({ offset: true }).nullable(),
  fcTransportType: transportTypeSchema.exclude(['EmailFallbackForPeppol']).nullable(),
  fcPeppolStatus: peppolStatusSchema.nullable(),
  fcPaidDate: z.iso.datetime({ offset: true }).nullable(),
  creditNoteDetails: z.null(),
  calculationData: calculationDataSchema,
  allowsAutomaticApproval: z.boolean(),
  hasPaymentFollowUp: z.boolean(),
  updatedAt: z.iso.datetime({ offset: true }),
});

const creditedInvoiceSchema = z.object({
  ...nonCreditedInvoiceSchema.shape,
  status: invoiceStatusSchema.extract(['Credited']),
  creditNoteDetails: z
    .object({
      fcEntryNumber: z.string(),
      reason: z.string(),
      documentDate: z.string(),
      coTransportType: transportTypeSchema.nullable(),
      coPeppolStatus: peppolStatusSchema.nullable(),
    })
    // Old credited invoices don't have a credit note
    .nullable(),
});

export const submittedInvoiceSchema = z.discriminatedUnion('status', [nonCreditedInvoiceSchema, creditedInvoiceSchema]);

export const invoiceSchema = z.discriminatedUnion('status', [draftInvoiceSchema, nonCreditedInvoiceSchema, creditedInvoiceSchema]);

export const invoiceFiltersSchema = z.object({
  clientIds: z.array(z.number()),
  brandIds: z.array(z.number()),
  years: z.array(z.number()),
  quarters: z.array(documentYearQuarterSchema),
  statuses: invoiceStatusSchema.array(),
});

export const creditableInvoiceSchema = z.object({
  id: z.number(),
  fcEntryNumber: z.string(),
});

const genericInvoiceHistoryRecordSchema = z.object({
  type: z.enum([
    'Created',
    'Finalized',
    'LockedByUser',
    'Expired',
    'PaidByClient',
    'PaidByCreativeShelter',
    'Credited',
    'CreditNoteApproved',
  ]),
  timestamp: z.string(),
  attributes: z.null(),
});

const approvedInvoiceHistoryRecordSchema = z.object({
  ...genericInvoiceHistoryRecordSchema.shape,
  type: z.literal('Approved'),
  attributes: z.object({
    approvalType: z.enum(['manual', 'automatic']),
  }),
});

const emailApprovedAndSentInvoiceHistoryRecordSchema = z.object({
  ...genericInvoiceHistoryRecordSchema.shape,
  type: z.enum(['EmailApprovedAndSent', 'CreditNoteEmailApprovedAndSent']),
  attributes: z.object({
    recipient: z.enum(['client', 'user']),
  }),
});

const emailPaymentReminderInvoiceHistoryRecordSchema = z.object({
  ...genericInvoiceHistoryRecordSchema.shape,
  type: z.literal('EmailPaymentReminder'),
  attributes: z.object({
    reminderType: z.enum(['first', 'second']),
  }),
});

const partiallyPaidInvoiceHistoryRecordSchema = z.object({
  ...genericInvoiceHistoryRecordSchema.shape,
  type: z.literal('PartiallyPaid'),
  attributes: z.object({
    amount: z.number(),
  }),
});

const peppolHistoryRecordSchema = z.object({
  ...genericInvoiceHistoryRecordSchema.shape,
  type: z.enum([
    'PeppolCoSent',
    'PeppolCoDelivered',
    'PeppolCoAccepted',
    'PeppolCoRefused',
    'PeppolFcSent',
    'PeppolFcDelivered',
    'PeppolFcAccepted',
    'PeppolFcRefused',
  ]),
  attributes: z.object({
    document: z.enum(['invoice', 'creditNote']),
  }),
});

const followUpStepInvoiceHistoryRecordSchema = z.object({
  ...genericInvoiceHistoryRecordSchema.shape,
  type: z.enum([
    'FollowUpStarted',
    'FollowUpManualReminder',
    'FollowUpContactClientByPhone',
    'FollowUpLastManualReminder',
    'FollowUpFormalNoticeSent',
    'FollowUpInvoiceSentToLawyer',
    'FollowUpInvoiceSentToBailiff',
  ]),
  attributes: z.object({
    remark: followUpRemarkSchema.nullable(),
  }),
});

const followUpRemarkInvoiceHistoryRecordSchema = z.object({
  ...genericInvoiceHistoryRecordSchema.shape,
  type: z.literal('FollowUpRemark'),
  attributes: z.object({
    remark: followUpRemarkSchema.nullable(),
  }),
});

export const invoiceHistoryRecordSchema = z.discriminatedUnion('type', [
  genericInvoiceHistoryRecordSchema,
  approvedInvoiceHistoryRecordSchema,
  emailApprovedAndSentInvoiceHistoryRecordSchema,
  emailPaymentReminderInvoiceHistoryRecordSchema,
  partiallyPaidInvoiceHistoryRecordSchema,
  peppolHistoryRecordSchema,
  followUpStepInvoiceHistoryRecordSchema,
  followUpRemarkInvoiceHistoryRecordSchema,
]);

export const invoiceHistorySchema = z.array(invoiceHistoryRecordSchema);
